/**
 * Utility functions for the application
 * All utility functions should be placed here to avoid scattered utility files
 */

/*
|--------------------------------------------------------------------------
| DATE HELPERS
|--------------------------------------------------------------------------
*/

const DATE_LOCALES: Record<string, string> = {
  en: 'en-US',
};

/**
 * Get the locale string for date formatting
 * @param lang - Language code
 * @returns Locale string for Intl APIs
 */
export const getDateLocale = (lang: string): string => {
  return DATE_LOCALES[lang] || lang;
};

/**
 * Convert a date string to UTC Date object
 * @param dateStr - Date string to convert
 * @returns UTC Date object
 */
export const utc = (dateStr: string): Date => {
  return new Date(new Date(dateStr).toUTCString().substring(0, 25));
};

/**
 * Format a date string using Date#toLocaleString
 * @param dateStr - The date string to format
 * @param locale - Locale for formatting (default: 'en-US')
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 * @example
 * ```ts
 * formatDate('2024-01-15') // "Jan 15, 2024"
 * ```
 */
export const formatDate = (
  dateStr: string,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {}
): string => {
  // w20-13562 – force en-sa to use en-us
  if (locale.toLowerCase() === 'en-sa') locale = 'en-US';

  let formattedDate;
  const date = new Date(utc(dateStr));
  const opts: Intl.DateTimeFormatOptions = {
    //default format --> 'Jan 23, 2022'
    year: 'numeric', //2022
    month: 'short', //Jan/Feb/Mar/etc. 'long' for full month, '2-digit' for 01-12
    day: '2-digit', //01-31, 'numeric' for 1-31
    ...options,
  };

  //timeStyle option cannot be used with opts presets, as per toLocaleString docs
  if ('timeStyle' in options) {
    formattedDate = date.toLocaleString(getDateLocale(locale), options);
  } else {
    formattedDate = date.toLocaleString(getDateLocale(locale), opts);
  }
  return formattedDate;
};

/**
 * Format a date string with ordered format (day month year)
 * Used for specific locales like en-sa that need "07 December 2023" format
 * @param dateStr - The date string to format
 * @param locale - Locale for formatting
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const orderedFormat = (
  dateStr: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = new Date(utc(dateStr));
  if (locale === 'en-sa') {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', options);
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } else {
    return formatDate(dateStr, locale, options);
  }
};

/**
 * Format a date string to a human-readable date and time format
 * @param dateString - The date string to format (ISO format or any valid date string)
 * @returns Formatted date string like "Dec 18, 2024, 03:30 PM" or '-' if invalid
 * @example
 * ```ts
 * formatDateTime('2024-12-18T15:30:00Z') // "Dec 18, 2024, 03:30 PM"
 * formatDateTime(null) // "-"
 * ```
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Calculate the number of days between a date and now
 * @param start - Start date string
 * @returns Number of days (negative if in the past, positive if in the future)
 * @example
 * ```ts
 * getDaysUntilNow('2024-12-25') // days until Christmas
 * ```
 */
export const getDaysUntilNow = (start: string): number => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const then = new Date(start).setHours(0, 0, 0, 0);
  const now = new Date().setHours(0, 0, 0, 0);
  const daysDifference = (then - now) / MS_PER_DAY;
  return daysDifference;
};

/*
|--------------------------------------------------------------------------
| LOCALE HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Converts URL-friendly locale format (lowercase) to Contentful format
 * @param locale - Locale in format like 'en-us', 'de-de', 'fr-ca'
 * @returns Locale in Contentful format like 'en-US', 'de-DE', 'fr-CA'
 * @example
 * ```ts
 * convertToContentfulLocale('en-us') // 'en-US'
 * convertToContentfulLocale('de-de') // 'de-DE'
 * ```
 */
export const convertToContentfulLocale = (locale: string): string => {
  const [language, country] = locale.split('-');
  return `${language}-${country.toUpperCase()}`;
};

/*
|--------------------------------------------------------------------------
| LOCAL STORAGE HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Get an item from localStorage with JSON parsing
 * @param key - The storage key
 * @returns The parsed value or null if not found/invalid
 * @example
 * ```ts
 * getStorageItem<UserIdentity>('user-identity')
 * ```
 */
export const getStorageItem = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (item) {
      return JSON.parse(item) as T;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

/**
 * Set an item in localStorage with JSON stringification
 * @param key - The storage key
 * @param value - The value to store
 * @example
 * ```ts
 * setStorageItem('user-identity', userIdentity)
 * ```
 */
export const setStorageItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Remove an item from localStorage
 * @param key - The storage key
 * @example
 * ```ts
 * removeStorageItem('user-identity')
 * ```
 */
export const removeStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Formats a string by replacing hyphens and underscores with spaces
 * and capitalizing the first letter of each word (Title Case)
 * @param str - The string to format
 * @returns Formatted string in Title Case
 * @example
 * ```ts
 * formatString('destination_charge') // "Destination Charge"
 * formatString('add-ons') // "Add Ons"
 * formatString('total price') // "Total Price"
 * formatString('hello') // "Hello"
 * ```
 */
export const formatString = (str: string | null | undefined): string => {
  if (!str) return '';
  return str
    .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format a number as currency with proper locale formatting
 * @param value - The numeric value to format
 * @param currencyCode - ISO 4217 currency code (default: 'USD')
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted currency string (e.g., "$1,234.56")
 * @example
 * ```ts
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(1000, 'EUR', 'de-DE') // "1.000,00 €"
 * ```
 */
export const formatCurrency = (value: number, currencyCode = 'USD', locale = 'en-US'): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(value);
};
